<script setup>
import axios from "axios";
import {computed, onMounted, ref} from "vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {changeStep, Steps} from "@/composables/ai-onboarding/useSteps";
import NavigationButtons from "@/components/ai-onboarding/partials/NavigationButtons.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import SweetToast from "@/SweetToast";
import useWebsite from "@/composables/useWebsite";
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import useAutoResize from "@/composables/useAutoResize";

const emit = defineEmits(['changeStep']);

const description = ref('');
const suggestedDescription = ref('');
const initialLoading = ref(true);
const {textareaRef, autoResize} = useAutoResize();

const minCharacters = 400;

const characterCount = computed(() => description.value?.length ?? 0);

onMounted(async () => {
  const {data} = await axios.get('/ajax/v2/ai-onboarding/business-description');

  suggestedDescription.value = data.data.suggestedDescription;
  description.value = data.data.currentDescription;

  // if no description, set the description to be suggested description
  if (!description.value) {
    description.value = suggestedDescription.value;
  }

  autoResize();
  initialLoading.value = false;
});

const { website } = useWebsite();
const queryClient = useQueryClient();

const {mutate: storeEmptyCompetitors, isPending: competitorsLoading} = useMutation({
  mutationFn: async () => await axios.post('/ajax/v2/ai-onboarding/competitors/store', {
    competitors: [],
  }),
  onSuccess: () => {
    queryClient.invalidateQueries(['competitor-suggestions']);
    queryClient.invalidateQueries(['current-website']);
  },
  onError: () => {
    throw new Error('Something went wrong');
  },
});

const {mutate: handleContinue, isPending} = useMutation({
  mutationFn: async () => await axios.post('/ajax/v2/ai-onboarding/business-description/store', {
    description: description.value
  }),
  onSuccess: () => {
    const nextStep = website.value.competitionCredits > 0 ? Steps.COMPETITORS : Steps.SERVICES_OFFERED;

    if (nextStep === Steps.SERVICES_OFFERED) {
      SweetToast.fire({
        icon: 'info',
        html: `Because you currently have no credits, we’ve skipped the Competitors step for now. <br/><br/> Don’t worry — you can add competitors later when you’re ready.`,
        timer: 8000,
      });

      storeEmptyCompetitors();
    }

    changeStep(nextStep, emit);
  },
  onError: () => {
    throw new Error('Something went wrong');
  },
});
</script>

<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-justify-between tw-h-full">
    <div class="tw-text-center tw-py-18 tw-px-14 tw-overflow-y-auto">
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-6">
        <div class="tw-flex tw-flex-col tw-gap-16 tw-items-center tw-w-full">
          <div class="tw-w-full">
            <LoadingBlock v-if="initialLoading" />

            <div
              v-else
              class="tw-flex tw-gap-8"
            >
              <div
                class="tw-flex tw-flex-col tw-justify-between tw-gap-y-6 tw-p-8 tw-border-2 tw-border-grey-mid tw-rounded-xl focus-within:tw-border-primary tw-w-full"
              >
                <div>
                  <textarea
                    ref="textareaRef"
                    v-model="description"
                    placeholder="Enter description here"
                    rows="10"
                    class="tw-w-full tw-text-slate-200 tw-text-base tw-overflow-hidden tw-resize-none"
                    @input="autoResize"
                  />
                </div>

                <div class="tw-flex tw-items-start tw-gap-2">
                  <span>
                    <svg-vue
                      icon="icons/warning-thin"
                      class="tw-w-6 tw-h-6 tw-text-primary"
                    />
                  </span>
                  <p class="tw-text-base tw-text-left tw-text-primary">
                    Remember to check your description is accurate and highlights your key products or services, the locations you serve, and details about your target customers.
                  </p>
                </div>
              </div>
            </div>

            <FadeTransition>
              <div
                v-if="!initialLoading && characterCount < minCharacters"
                class="tw-mt-4 tw-flex tw-gap-4 tw-items-center tw-justify-end"
              >
                <span class="tw-text-grey-dark tw-text-base">
                  Please ensure your input has at least 400 characters. Currently, you've entered
                </span>
                <div>
                  <span class="tw-text-base tw-font-bold tw-text-red">
                    {{ characterCount }}
                  </span>
                  <span class="tw-text-grey-dark tw-font-bold tw-text-base">
                    / {{ minCharacters }}
                  </span>
                </div>
              </div>
            </FadeTransition>
          </div>
        </div>
      </div>
    </div>

    <NavigationButtons
      :disable-continue="characterCount < minCharacters"
      :loading="isPending || competitorsLoading"
      @back="changeStep(Steps.LOCATIONS, emit)"
      @next="handleContinue"
    />
  </div>
</template>
